import React, { useEffect, useMemo, useState } from 'react';
import { ProtectedRoute } from '../../../components/ProtectedRoute';
import { AsyncBoundary } from '../../../components/AsyncBoundary';
import { CategoryStatCard, CategoryTable, AddCategoryModal, EditCategoryModal } from './components';
import { CategoryStats, CategoryItem } from './categoriesTypes';
import { useCategoriesStore } from '../../../store/categoriesStore';
import { Category } from '../../../services/categoriesApi';
import styles from './AdminCategories.module.css';

function adapt(c: Category): CategoryItem {
  return {
    id: String(c.id),
    name: c.name,
    skuCount: c.sku_count,
    totalStock: c.total_stock,
    revenue: Math.round(c.total_revenue),
    avgMargin: c.avg_margin,
    fastMovers: 0, // not tracked yet
    growth: 0,    // not tracked yet
  };
}

const AdminCategories: React.FC = () => {
  const items = useCategoriesStore((s) => s.items);
  const loading = useCategoriesStore((s) => s.loading);
  const error = useCategoriesStore((s) => s.error);
  const fetchList = useCategoriesStore((s) => s.fetchList);
  const remove = useCategoriesStore((s) => s.remove);
  const [showAddModal, setShowAddModal] = useState(false);
  const [editing, setEditing] = useState<Category | null>(null);

  useEffect(() => { fetchList(); }, [fetchList]);

  const handleEdit = (id: string) => {
    const cat = items.find((c) => String(c.id) === id);
    if (cat) setEditing(cat);
  };

  const handleDelete = async (id: string) => {
    const cat = items.find((c) => String(c.id) === id);
    if (!cat) return;
    if (!window.confirm(`Delete category "${cat.name}"? This cannot be undone.`)) return;
    try {
      await remove(cat.id);
    } catch (err) {
      window.alert(err instanceof Error ? err.message : 'Failed to delete category');
    }
  };

  const adapted = useMemo<CategoryItem[]>(() => items.map(adapt), [items]);

  const stats: CategoryStats = {
    totalCategories: items.length,
    totalSkus: items.reduce((sum, c) => sum + c.sku_count, 0),
    totalRevenue: `$${Math.round(items.reduce((sum, c) => sum + c.total_revenue, 0)).toLocaleString()}`,
  };

  return (
    <div className={styles.container}>
      <div className={styles.sectionHeader}>
        <div>
          <h2 className={styles.sectionTitle}>Category Analysis</h2>
          <p className={styles.sectionSubtitle}>Track performance across product categories</p>
        </div>
        <button className={styles.addBtn} onClick={() => setShowAddModal(true)}>
          <svg viewBox="0 0 24 24" fill="currentColor"><path d="M19 13h-6v6h-2v-6H5v-2h6V5h2v6h6v2z"/></svg>
          Add Category
        </button>
      </div>

      <div className={styles.statsGrid}>
        <CategoryStatCard title="Total Categories" value={String(stats.totalCategories)} icon="categories" color="blue" />
        <CategoryStatCard title="Total SKUs" value={String(stats.totalSkus)} icon="skus" color="green" />
        <CategoryStatCard title="Total Revenue" value={stats.totalRevenue} icon="revenue" color="teal" />
      </div>

      <AsyncBoundary
        loading={loading && items.length === 0}
        error={error}
        empty={!loading && adapted.length === 0}
        emptyMessage="No categories yet. Create one to get started."
        onRetry={() => fetchList()}
      >
        <CategoryTable items={adapted} onEdit={handleEdit} onDelete={handleDelete} />
      </AsyncBoundary>

      <AddCategoryModal open={showAddModal} onClose={() => setShowAddModal(false)} />
      <EditCategoryModal open={!!editing} category={editing} onClose={() => setEditing(null)} />
    </div>
  );
};

export const AdminCategoriesPage: React.FC = () => (
  <ProtectedRoute requiredRoles={['admin']}>
    <AdminCategories />
  </ProtectedRoute>
);

export { AdminCategories };
export default AdminCategories;
